import * as React from 'react';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import DateRangePicker from 'react-bootstrap-daterangepicker';

import classNames from 'classnames';
import $ from 'jquery';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import a11yKeyPress from 'js/lib/a11yKeyPress';
import { MED_DATETIME_NO_YEAR_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { SvgButton, color } from '@coursera/coursera-ui';
import { SvgFail } from '@coursera/coursera-ui/svg';

import { invalidateDates, validateDates } from 'bundles/author-sessions/actions/SessionItemActions';
import type SessionItem from 'bundles/author-sessions/models/SessionItem';
import { FloatingTooltip, TooltipWrapper } from 'bundles/common/components/Tooltip';

import _t from 'i18n!nls/author-sessions';

import 'css!./__styles__/DeadlinePicker';

const minimumVerticalDistance = 350;

type Props = {
  date: moment.Moment;
  defaultDate: moment.Moment;
  maxDate: moment.Moment;
  minDate: moment.Moment;
  onChange: (x1: moment.Moment | undefined) => void;
  onShow: () => any;
  onHide: () => any;
  itemId: string;
  itemAvailability: any;
  item: SessionItem;
  sessionId: string;
  readOnly?: boolean;
  isClearable?: boolean;
  openDirection?: string;
  dropDirection?: string;
  getClientYOnClick: (event: $TSFixMe) => void;
  clientY: number;
  parentEl?: string;
};

type State = {
  dateMoment: moment.Moment | undefined;
  invalidDate: boolean;
};

class DeadlinePicker extends React.Component<Props, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof DeadlinePicker.contextTypes>;

  static defaultProps = {
    openDirection: 'center',
    dropDirection: 'up',
    isClearable: false,
  };

  constructor(props: Props) {
    import('css!bootstrap-daterangepicker/daterangepicker.css');
    super(props);
    this.state = {
      dateMoment: props.date,
      invalidDate: false,
    };
  }

  componentDidMount() {
    this.validateDate({ date: this.state.dateMoment });
  }

  componentWillReceiveProps(nextProps: Props) {
    const { date } = this.props;
    const { dateMoment } = this.state;

    if (nextProps.date && !nextProps.date.isSame(dateMoment, 'minute')) {
      this.setState({ dateMoment: moment(nextProps.date) });
    }

    // if there is no prop date and no date override or if the prop for the date changed to become null, set the date to be the default
    if (!nextProps.date && (!dateMoment || date)) {
      this.setState({ dateMoment: moment(nextProps.defaultDate) });
    }
  }

  handleDateChange = (e: $TSFixMe, picker: $TSFixMe) => {
    const date = picker.startDate;

    if (date) {
      this.validateDate({ date }, true);
    }
  };

  hasValidDate({ maxDate, hardDeadline }: { maxDate: moment.Moment; hardDeadline: moment.Moment }) {
    return hardDeadline ? !hardDeadline.isAfter(maxDate) : true;
  }

  validateDate = ({ date }: { date?: moment.Moment | undefined }, callOnChange = false) => {
    const { item, maxDate, onChange, sessionId, itemAvailability } = this.props;
    const hardDeadline = item.isIncrementalLatePenalty && item.getHardDeadlineDate(date, itemAvailability);

    // perform validations on currently set dates, and set the error/success states accordingly
    if (this.hasValidDate({ maxDate, hardDeadline })) {
      this.setState({ dateMoment: date, invalidDate: false });
      this.context.executeAction(validateDates, { sessionId });

      if (callOnChange) {
        onChange(date);
      }
    } else {
      this.setState({ dateMoment: date, invalidDate: true });
      this.context.executeAction(invalidateDates, { sessionId, message: 'Hard deadline cannot be after the end date' });
    }
  };

  noop() {}

  removeDatePickerClassName = (className: string) => {
    const datePickerNodeList = document.querySelectorAll('.daterangepicker.dropdown-menu.ltr');

    datePickerNodeList.forEach((element) => {
      element.classList.remove(className);
    });
  };

  addClassToOpenPicker = (pickerData: $TSFixMe) => {
    const pickerContainer = pickerData.container;

    this.removeDatePickerClassName('open-picker');
    pickerContainer.addClass('open-picker');
  };

  handleCustomDate = (date: moment.Moment) => {
    // get dates from the datepicker jquery object. we need this since the library does not provide
    // an event listener for when a date is selected, but only for when a date is applied using 'onApply'.
    // By pulling out the date from here, we can add custom styles at the moment of a new date selection.
    const dp = $(`#deadline-picker-${this.props.itemId}`);

    if (!dp) {
      return null;
    }

    const pickerData = dp.parent().data('daterangepicker');
    const newStartDate = pickerData.startDate;

    this.addClassToOpenPicker(pickerData);

    const { itemAvailability, item, minDate } = this.props;
    const hardDeadline = item.isIncrementalLatePenalty && item.getHardDeadlineDate(newStartDate, itemAvailability);

    const classes = [];

    if (itemAvailability) {
      const availabilityStartDate = itemAvailability.startDate;
      const availabilityEndDate = itemAvailability.endDate;

      // show availability window
      if (
        date.isSame(availabilityStartDate, 'day') ||
        date.isBetween(availabilityStartDate, availabilityEndDate, 'day') ||
        date.isSame(availabilityEndDate, 'day')
      ) {
        classes.push('availability-date');
      }

      if (availabilityStartDate.isAfter(newStartDate)) {
        classes.push('invalid-start-date');
      }

      if (date.isSame(availabilityEndDate, 'day')) {
        classes.push('availability-end-date');
      }
    }

    if (date.isSame(newStartDate, 'day')) {
      classes.push('item-deadline-date');
    }

    if (date.isSame(minDate, 'day')) {
      classes.push('week-start-date');
    }

    if (date.isBefore(minDate, 'day')) {
      classes.push('before-week-start-date');
    }

    if (hardDeadline) {
      if (date.isSame(hardDeadline, 'day')) {
        classes.push('deadline-hard-date');
      } else if (date.isAfter(newStartDate, 'day') && date.isBefore(hardDeadline, 'day')) {
        classes.push('late-penalty-date');
      }
    }

    setTimeout(() => {
      // force datepicker.move() to set the correct position when it is opened
      pickerData.move();
    }, 0);

    return classes;
  };

  handleClearDeadline = () => {
    this.setState({ dateMoment: undefined });
    this.props.onChange(undefined);
  };

  renderClearDeadline() {
    const title = _t('Clear deadline');
    return (
      <TooltipWrapper message={title} placement="bottom">
        <SvgButton
          type="icon"
          size="zero"
          rootClassName="m-l-1 clear-deadline-button"
          onClick={this.handleClearDeadline}
          isTransparent
          svgElement={<SvgFail color={color.secondaryText} hoverColor={color.primary} size={12} title={title} />}
          htmlAttributes={{ title }}
        />
      </TooltipWrapper>
    );
  }

  render() {
    const { dateMoment, invalidDate } = this.state;
    const {
      maxDate,
      minDate,
      defaultDate,
      itemId,
      onShow,
      onHide,
      readOnly,
      openDirection,
      dropDirection,
      isClearable,
      clientY,
      getClientYOnClick,
      parentEl,
    } = this.props;
    const wrapperClasses = classNames('rc-DeadlinePicker horizontal-box align-items-vertical-center', {
      'invalid-date': invalidDate,
    });
    const displayedDate = dateMoment || defaultDate;
    const pickerDropDirection = clientY < minimumVerticalDistance ? 'down' : dropDirection;

    // Saving this runtime guard while Item in ItemTimedReleaseDeadlineEditor is not strongly typed.
    // If it ever becomes strongly typed, then we can remove this.
    // @ts-expect-error
    if (isNaN(displayedDate)) {
      return null;
    }

    if (readOnly) {
      return (
        <TooltipWrapper placement="bottom" message={_t('Editing not allowed until course launch')}>
          <div className="horizontal-box">
            {displayedDate ? formatDateTimeDisplay(displayedDate, MED_DATETIME_NO_YEAR_DISPLAY) : '-'}
          </div>
        </TooltipWrapper>
      );
    }

    return (
      <div className={wrapperClasses}>
        <DateRangePicker
          startDate={displayedDate || minDate}
          singleDatePicker={true}
          minDate={minDate}
          maxDate={maxDate}
          onApply={this.handleDateChange}
          onCancel={this.noop}
          isCustomDate={displayedDate ? this.handleCustomDate : null}
          onShow={onShow}
          onHide={onHide}
          opens={openDirection}
          drops={pickerDropDirection}
          applyClass="teach-btn primary m-r-1s"
          cancelClass="teach-btn secondary"
          timePicker={true}
          parentEl={parentEl} // jQuery selector of parent element; DateRangePicker uses body by default if not provided
        >
          <FloatingTooltip placement="bottom" message={_t('Edit Item Deadline')}>
            <div
              className="picker-input"
              id={`deadline-picker-${itemId}`}
              role="button"
              tabIndex={-1} // added because the tabbing through the course session row automatically opens up the picker
              onClick={getClientYOnClick}
              onKeyDown={(event) => a11yKeyPress(event, getClientYOnClick)}
            >
              {!displayedDate && '-'}
              {displayedDate && formatDateTimeDisplay(displayedDate, MED_DATETIME_NO_YEAR_DISPLAY)}
            </div>
          </FloatingTooltip>
        </DateRangePicker>
        {isClearable && this.renderClearDeadline()}
      </div>
    );
  }
}

export default DeadlinePicker;
